import React from "react";
import { Input } from "./Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./Select";
import { Textarea } from "./Textarea";
import { Field, SectionHeading } from "./Field";

export type SessionFormValue = {
  lake_id: string;
  peg: string;
  start_at: string;
  end_at: string;
  rods_count: string;
  target_species: string;
  weather: string;
  rain_observed: string;
  pressure_trend: string;
  air_temp_c: string;
  wind_direction: string;
  wind_speed_mph: string;
  air_pressure_hpa: string;
  cloud_cover_pct: string;
  water_temp_c: string;
  gps_latitude: string;
  gps_longitude: string;
  privacy_setting: string;
  share_peg_anonymously: boolean;
  notes: string;
};

export function SessionFields({
  values,
  setValues,
  lakes,
  rodsEditable = true,
  sessionRodsCount,
  rodsLabel = "Number of rods",
  rodsHelp,
}: {
  values: SessionFormValue;
  setValues: React.Dispatch<React.SetStateAction<SessionFormValue>>;
  lakes: any[];
  rodsEditable?: boolean;
  sessionRodsCount?: number;
  rodsLabel?: string;
  rodsHelp?: string;
}) {
  const update = (key: keyof SessionFormValue, value: string) =>
    setValues((current) => ({ ...current, [key]: value }));
  const selectedLake = lakes.find(
    (lake) => String(lake.id) === String(values.lake_id),
  );

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <SectionHeading>Session basics</SectionHeading>
        <div className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-inset p-3 sm:grid-cols-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-secondary">Water</p>
            <p className="mt-1 text-sm font-semibold text-primary">
              {selectedLake?.name || "Selected water"}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-secondary">Peg / swim</p>
            <p className="mt-1 text-sm font-semibold text-primary">
              {values.peg || "Not recorded"}
            </p>
          </div>
        </div>
        {rodsEditable ? (
          <p className="text-xs leading-5 text-secondary">
            Water and peg are not free-text edits here. Use Start Fishing for a new session, or Move peg from the live session so CatchCompass preserves the correct timed peg history.
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Start date and time" id="session-start">
            <Input
              id="session-start"
              type="datetime-local"
              value={values.start_at}
              onChange={(e) => update("start_at", e.target.value)}
              required
            />
          </Field>
          {values.end_at ? (
            <Field label="Finish date and time" id="session-end">
              <Input
                id="session-end"
                type="datetime-local"
                value={values.end_at}
                onChange={(e) => update("end_at", e.target.value)}
              />
            </Field>
          ) : null}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {rodsEditable ? (
            <Field label={rodsLabel} id="session-rods" help={rodsHelp}>
              <Input
                id="session-rods"
                type="number"
                min="1"
                max="20"
                value={values.rods_count}
                onChange={(e) => update("rods_count", e.target.value)}
              />
            </Field>
          ) : (
            <Field
              label="Total rods"
              id="session-rods"
              help="Historical rod count. Guest/angler rod numbers are managed separately."
            >
              <div
                id="session-rods"
                className="flex h-10 items-center rounded-md border border-border bg-inset px-3 text-sm text-secondary"
              >
                {sessionRodsCount ?? "—"}
              </div>
            </Field>
          )}
          <Field label="Target species" id="session-target-species">
            <Select
              value={values.target_species}
              onValueChange={(value) => update("target_species", value)}
            >
              <SelectTrigger id="session-target-species">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Both</SelectItem>
                <SelectItem value="carp">Carp</SelectItem>
                <SelectItem value="catfish">Catfish</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeading>Privacy</SectionHeading>
        <Field label="Session privacy" id="session-privacy">
          <Select
            value={values.privacy_setting}
            onValueChange={(value) => update("privacy_setting", value)}
          >
            <SelectTrigger id="session-privacy">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Private">Private</SelectItem>
              <SelectItem value="Contribute anonymously to CarpIntel statistics">
                Contribute anonymously
              </SelectItem>
              <SelectItem value="Shared">Shared</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <label className="flex items-center gap-2 text-sm text-secondary">
          <input
            type="checkbox"
            checked={values.share_peg_anonymously}
            onChange={(e) =>
              setValues((current) => ({
                ...current,
                share_peg_anonymously: e.target.checked,
              }))
            }
          />
          Allow this peg name to contribute anonymously
        </label>
      </section>

      <section className="space-y-4">
        <SectionHeading>Session notes</SectionHeading>
        <Field label="General notes" id="session-notes">
          <Textarea
            id="session-notes"
            rows={5}
            value={values.notes}
            onChange={(e) => update("notes", e.target.value)}
            placeholder="What did you notice on the bank?"
          />
        </Field>
      </section>

      <details className="rounded-xl border border-border bg-raised">
        <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-secondary">
          Advanced: correct recorded conditions
        </summary>
        <div className="space-y-4 border-t border-border p-4">
          <p className="text-xs leading-5 text-secondary">
            CatchCompass normally records weather and location automatically. Only use these fields to correct a bad historical reading.
          </p>
          <Field label="Weather" id="session-weather">
            <Input
              id="session-weather"
              value={values.weather}
              onChange={(e) => update("weather", e.target.value)}
              placeholder="e.g. Overcast and drizzle"
            />
          </Field>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Rain" id="session-rain">
              <Select
                value={values.rain_observed}
                onValueChange={(value) => update("rain_observed", value)}
              >
                <SelectTrigger id="session-rain"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="not-recorded">Not recorded</SelectItem>
                  <SelectItem value="true">Rain or drizzle</SelectItem>
                  <SelectItem value="false">Dry</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Pressure trend" id="session-pressure-trend">
              <Select
                value={values.pressure_trend}
                onValueChange={(value) => update("pressure_trend", value)}
              >
                <SelectTrigger id="session-pressure-trend"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="not-recorded">Not recorded</SelectItem>
                  <SelectItem value="rising">Rising</SelectItem>
                  <SelectItem value="falling">Falling</SelectItem>
                  <SelectItem value="stable">Stable</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Air temperature (°C)" id="air-temp">
              <Input id="air-temp" type="number" step="0.1" min="-30" max="50" value={values.air_temp_c} onChange={(e) => update("air_temp_c", e.target.value)} />
            </Field>
            <Field label="Water temperature (°C)" id="water-temp">
              <Input id="water-temp" type="number" step="0.1" min="-5" max="40" value={values.water_temp_c} onChange={(e) => update("water_temp_c", e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Wind direction" id="wind-direction">
              <Input id="wind-direction" value={values.wind_direction} onChange={(e) => update("wind_direction", e.target.value)} />
            </Field>
            <Field label="Wind speed (mph)" id="wind-speed">
              <Input id="wind-speed" type="number" step="0.1" min="0" max="150" value={values.wind_speed_mph} onChange={(e) => update("wind_speed_mph", e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Air pressure (hPa)" id="air-pressure">
              <Input id="air-pressure" type="number" step="0.1" min="850" max="1100" value={values.air_pressure_hpa} onChange={(e) => update("air_pressure_hpa", e.target.value)} />
            </Field>
            <Field label="Cloud cover (%)" id="cloud-cover">
              <Input id="cloud-cover" type="number" min="0" max="100" value={values.cloud_cover_pct} onChange={(e) => update("cloud_cover_pct", e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Private GPS latitude" id="session-gps-latitude">
              <Input id="session-gps-latitude" type="number" step="0.000001" value={values.gps_latitude} onChange={(e) => update("gps_latitude", e.target.value)} />
            </Field>
            <Field label="Private GPS longitude" id="session-gps-longitude">
              <Input id="session-gps-longitude" type="number" step="0.000001" value={values.gps_longitude} onChange={(e) => update("gps_longitude", e.target.value)} />
            </Field>
          </div>
        </div>
      </details>
    </div>
  );
}
